import {
  Alignment,
  Callout,
  Card,
  Classes,
  Collapse,
  Icon,
  Intent,
  Label,
  PanelProps,
  Position,
  Spinner,
  SpinnerSize,
  Switch,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { createSelector } from '@reduxjs/toolkit';
import * as React from 'react';
import { Text, Tooltip } from 'vet-bones/components';
import { usePortalConnections } from 'vet-bones/utils/portal';

import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { refreshDatabase } from 'src/actions/databases/action-creators/refreshDatabase';
import { refreshDatabases } from 'src/actions/databases/action-creators/refreshDatabases';
import { DatabaseSelector } from 'src/components/DatabaseSelector';
import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { TEST_IDS } from 'src/constants/testIds';
import { IntersectedEmptyObject } from 'src/constants/types';
import { ModelConfigPanel } from 'src/containers/PublishProjectDrawer/ModelConfigPanel';
import { PublishProjectDrawerFooter } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerFooter';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import { ConnectionInfo } from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { databaseSelector } from 'src/selectors/database';
import { databaseItemsSelector } from 'src/selectors/databaseItems';
import { databaseNamespacesSelector } from 'src/selectors/databaseNamespaces';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import { projectSelector } from 'src/selectors/project';
import { projectPublishHistorySelector } from 'src/selectors/projectPublishHistory';
import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import {
  Connection,
  coercePortalConnection,
} from 'src/store/connections/types';
import { Project } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { replaceInvalidIriChars } from 'src/utils/removeInvalidIriChars';
import { validateIri, validatePrefix } from 'src/utils/validateIri';

export const getDefaultNamespacePrefix = (project?: Project) =>
  replaceInvalidIriChars(project?.name);
export const getDefaultNamespaceIri = (project?: Project) => {
  return project?.modelNamespace || '';
};

const isDatabaseReadySelector = (databaseId: string | null) =>
  createSelector(
    databaseSelector(databaseId),
    isDatabaseRefreshedSelector(databaseId),
    (database, isRefreshed) => Boolean(!database || isRefreshed)
  );

export const ConnectionConfigPanel: React.VFC<
  PanelProps<IntersectedEmptyObject>
> = ({ openPanel }) => {
  const dispatch = useReduxDispatch();

  const connection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const didIsConnectedChanged = useDidValueChange(isConnected);
  const connections = usePortalConnections();

  const databaseItems = useReduxSelector(databaseItemsSelector);
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);
  const didIsRefreshingDatabasesChanged = useDidValueChange(
    isRefreshingDatabases
  );

  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const [databaseId, setDatabaseId] = React.useState<string | null>(null);
  const isDatabaseReady = useReduxSelector(isDatabaseReadySelector(databaseId));

  const disableAddNamespace = !databaseId || !isDatabaseReady;
  const didDisableAddNamespaceChanged = useDidValueChange(disableAddNamespace);

  const databaseNamespaces = useReduxSelector(
    databaseNamespacesSelector(databaseId)
  );
  const [shouldAddNamespace, setShouldAddNamespace] = React.useState(true);
  const [namespacePrefix, setNamespacePrefix] = React.useState(
    getDefaultNamespacePrefix(project)
  );
  const [namespaceIri, setNamespaceIri] = React.useState(
    getDefaultNamespaceIri(project)
  );
  const addNamespaceToggle = !disableAddNamespace && shouldAddNamespace;
  const existingNamespaceIri = databaseNamespaces?.[namespacePrefix];
  const showNamespaceOverwriteWarning =
    existingNamespaceIri !== undefined && existingNamespaceIri !== namespaceIri;

  const portalConnections = usePortalConnections();
  const defaultPortalConnection = useDefaultPortalConnection();

  const publishHistory = useReduxSelector(
    projectPublishHistorySelector(projectId)
  );

  // Select default connection from publish history, or default portal connection
  const didConnection = React.useRef(false);
  React.useEffect(() => {
    if (
      didConnection.current ||
      !portalConnections.data ||
      !defaultPortalConnection
    ) {
      return;
    }
    didConnection.current = true;

    const wantedConnection =
      portalConnections.data.find(
        ({ id }) => id === publishHistory?.connectionId
      ) || defaultPortalConnection;
    dispatch(setupPortalConnection(coercePortalConnection(wantedConnection)));
  }, [
    connection,
    defaultPortalConnection,
    didConnection,
    dispatch,
    portalConnections,
    publishHistory,
  ]);

  // Update default namespace IRI on database change
  React.useEffect(() => {
    setNamespaceIri(getDefaultNamespaceIri(project));
  }, [databaseNamespaces, project]);

  // Update databases and data source type descriptions on connection change
  React.useEffect(() => {
    if (didIsConnectedChanged && isConnected && connection) {
      dispatch(refreshDatabases()).then(() => {
        if (databaseId) {
          dispatch(refreshDatabase(databaseId));
        }
      });
      dataSourcesStardogRequestDispatchers.requestDataSourceTypeDescription(
        connection
      );
    }
  }, [connection, databaseId, didIsConnectedChanged, dispatch, isConnected]);

  // Select default database (from publish history), after loading databases
  React.useEffect(() => {
    if (!project) {
      return;
    }
    const {
      connectionInfo: { databaseId },
    } = project.publishHistory;
    if (
      didIsRefreshingDatabasesChanged &&
      databaseId &&
      isConnected &&
      !isRefreshingDatabases &&
      databaseItems.find(({ value }) => value === databaseId)
    ) {
      setDatabaseId(databaseId);
      dispatch(refreshDatabase(databaseId));
    }
  }, [
    databaseItems,
    didIsRefreshingDatabasesChanged,
    dispatch,
    isConnected,
    isRefreshingDatabases,
    project,
  ]);

  // Set namespace fields if publish history had them
  React.useEffect(() => {
    if (!project) {
      return;
    }
    const {
      connectionInfo: { namespaceIri, namespacePrefix, shouldAddNamespace },
    } = project.publishHistory;
    if (didDisableAddNamespaceChanged && !disableAddNamespace && namespaceIri) {
      setShouldAddNamespace(shouldAddNamespace);
      setNamespaceIri(namespaceIri);
      setNamespacePrefix(namespacePrefix);
    }
  }, [didDisableAddNamespaceChanged, disableAddNamespace, project]);

  const handleCancel = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
  }, [dispatch]);

  const nextButtonError = React.useMemo(() => {
    if (!isConnected) {
      return copy.components.publishProjectDrawer.validation.connection;
    }
    if (!databaseId) {
      return copy.components.publishProjectDrawer.validation.database;
    }
    if (!isDatabaseReady) {
      return copy.components.publishProjectDrawer.validation.refreshDatabase;
    }
    if (
      shouldAddNamespace &&
      (validatePrefix(namespacePrefix) !== '' ||
        validateIri(namespaceIri) !== '')
    ) {
      return copy.components.publishProjectDrawer.validation.namespace;
    }
    return '';
  }, [
    shouldAddNamespace,
    databaseId,
    isConnected,
    isDatabaseReady,
    namespaceIri,
    namespacePrefix,
  ]);

  const handleNext = React.useCallback(() => {
    if (nextButtonError || !databaseId) {
      return;
    }
    const connectionInfo: ConnectionInfo = {
      databaseId,
      namespacePrefix,
      namespaceIri,
      shouldAddNamespace,
    };
    openPanel({
      renderPanel: ModelConfigPanel,
      props: {
        connectionInfo,
      },
    });
  }, [
    databaseId,
    namespaceIri,
    namespacePrefix,
    nextButtonError,
    openPanel,
    shouldAddNamespace,
  ]);

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.connection.container}
      >
        <PublishProjectDrawerStepper active="connection" />
        {connections.data && connections.data.length > 1 ? (
          <Label>
            {
              copy.components.publishProjectDrawer.connectionConfiguration
                .serverSectionHeader
            }
            <ServerSelection />
          </Label>
        ) : null}

        <Label>
          {
            copy.components.publishProjectDrawer.connectionConfiguration
              .databaseSectionHeader
          }
          <div className="sd-drawer-selector-menu">
            <DatabaseSelector
              creatable
              databaseId={databaseId}
              popoverPosition={Position.BOTTOM_LEFT}
              setDatabaseId={setDatabaseId}
            />
          </div>
        </Label>

        <div>
          <Switch
            alignIndicator={Alignment.RIGHT}
            checked={addNamespaceToggle}
            className="sd-label-switch"
            data-testid={
              TEST_IDS.publishProjectDrawer.connection.addNamespace.switch
            }
            disabled={disableAddNamespace}
            inline
            label={
              copy.components.publishProjectDrawer.connectionConfiguration
                .namespaceSection.header
            }
            onChange={() => setShouldAddNamespace((prevValue) => !prevValue)}
          />
          <div
            className="sd-drawer-margin"
            data-testid={
              TEST_IDS.publishProjectDrawer.connection.addNamespace.container
            }
          >
            <Collapse isOpen={addNamespaceToggle}>
              <InlineEditableLabel
                key={`${databaseId}-${namespaceIri}`} // reset error state on database / iri change
                disabled={disableAddNamespace}
                label={
                  copy.components.publishProjectDrawer.connectionConfiguration
                    .namespaceSection.prefix
                }
                onSetValue={setNamespacePrefix}
                onValidate={validatePrefix}
                placeholder=""
                testId={
                  TEST_IDS.publishProjectDrawer.connection.addNamespace.prefix
                }
                value={namespacePrefix}
              />
              <InlineEditableLabel
                disabled={disableAddNamespace}
                label={
                  <>
                    {
                      copy.components.publishProjectDrawer
                        .connectionConfiguration.namespaceSection.iri
                    }{' '}
                    <Tooltip
                      content={
                        copy.components.publishProjectDrawer
                          .connectionConfiguration.namespaceSection
                          .iriFieldWarning
                      }
                      disabled={disableAddNamespace}
                      position={Position.TOP}
                    >
                      <Icon icon={IconNames.INFO_SIGN} />
                    </Tooltip>
                  </>
                }
                onSetValue={setNamespaceIri}
                onValidate={validateIri}
                testId={
                  TEST_IDS.publishProjectDrawer.connection.addNamespace.iri
                }
                value={namespaceIri}
              />
              <Collapse isOpen={showNamespaceOverwriteWarning}>
                <Callout
                  className="sd-drawer-callout-reset"
                  data-testid={
                    TEST_IDS.publishProjectDrawer.connection.addNamespace
                      .overwriteWarning
                  }
                  intent={Intent.WARNING}
                  title={
                    copy.components.publishProjectDrawer.connectionConfiguration
                      .namespaceSection.overwriteWarningTitle
                  }
                >
                  {copy.components.publishProjectDrawer.connectionConfiguration.namespaceSection.overwriteWarningMessage(
                    namespacePrefix ||
                      copy.components.publishProjectDrawer
                        .connectionConfiguration.namespaceSection.prefixEmpty,
                    databaseNamespaces?.[namespacePrefix]
                  )}
                </Callout>
              </Collapse>
            </Collapse>
          </div>
        </div>
      </div>

      <PublishProjectDrawerFooter
        handleBack={handleCancel}
        handleNext={handleNext}
        isFirstPanel
        nextError={nextButtonError}
        nextTestId={TEST_IDS.publishProjectDrawer.footer.nextToModelButton}
        nextText={copy.components.publishProjectDrawer.footer.next}
      />
    </>
  );
};

const ServerSelection = () => {
  const portalConnections = usePortalConnections();
  const isFirstTimeFetching =
    portalConnections.isFetching && !portalConnections.data;

  return isFirstTimeFetching ? (
    <Spinner />
  ) : (
    <div data-testid={TEST_IDS.publishProjectDrawer.connection.serverSelect}>
      {portalConnections.data?.map((connection) => (
        <ConnectionItem
          key={connection.id}
          connection={coercePortalConnection(connection)}
        />
      ))}
    </div>
  );
};

const ConnectionItem = ({ connection }: { connection: Connection }) => {
  const dispatch = useReduxDispatch();

  const currentConnection = useReduxSelector(connectionSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isConnected = useReduxSelector(isConnectedSelector);

  const disabled = isConnecting;

  const handleClick = React.useCallback(() => {
    if (disabled) {
      return;
    }

    dispatch(setupPortalConnection(connection));
  }, [connection, disabled, dispatch]);

  return (
    <Card
      className={Classes.CALLOUT}
      interactive={!disabled}
      onClick={handleClick}
    >
      {isConnecting && currentConnection?.id === connection.id ? (
        <Spinner size={SpinnerSize.SMALL} />
      ) : (
        <Icon
          icon={
            isConnected && currentConnection?.id === connection.id
              ? IconNames.SELECTION
              : IconNames.CIRCLE
          }
          intent={disabled ? Intent.NONE : Intent.PRIMARY}
          size={SpinnerSize.SMALL}
        />
      )}
      <div>
        <Text>{connection.name}</Text>
        <Text>
          {connection.username}@{connection.endpoint}
        </Text>
      </div>
    </Card>
  );
};
